<template>
  <transition name="slide-fade" appear>
    <aside
      v-if="content"
      class="promotional-bar"
      :class="[fixed ? 'promotional-bar--fixed' : '', toneClass]"
      role="region"
      :aria-label="ariaLabel"
      aria-live="polite"
    >
      <!-- brillo/flare decorativo -->
      <span class="promo-flare" aria-hidden="true"></span>

      <div class="promo-inner">
        <!-- Icono -->
        <div class="promo-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path fill="currentColor"
              d="M14 3l7 7-4 1-4 4-1 4-7-7 4-1 4-4 1-4zm-9.1 9.1l-1.8.5a1 1 0 0 0-.6 1.6l5.3 5.3a1 1 0 0 0 1.6-.6l.5-1.8-5-5z"/>
          </svg>
        </div>

        <!-- Texto -->
        <p class="promo-text">
          {{ content.text }}
        </p>

        <!-- CTA interno -->
        <RouterLink
          v-if="!isExternal"
          class="promo-cta"
          :to="linkUrl"
          :aria-label="content.linkText + ' (' + internalLabel + ')'"
        >
          <span class="promo-cta__label">{{ content.linkText }}</span>
          <svg class="promo-cta__icon" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M5 12h12m0 0l-5-5m5 5l-5 5"/>
          </svg>
        </RouterLink>

        <!-- CTA externo -->
        <a
          v-else
          class="promo-cta"
          :href="linkUrl"
          target="_blank"
          rel="noopener"
          :aria-label="content.linkText + ' (' + externalLabel + ')'"
        >
          <span class="promo-cta__label">{{ content.linkText }}</span>
          <svg class="promo-cta__icon" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M5 12h12m0 0l-5-5m5 5l-5 5"/>
          </svg>
        </a>
      </div>
    </aside>
  </transition>
</template>

<script>
import { RouterLink } from 'vue-router'
import { promotionalText } from '@/assets/data/data.ts'

export default {
  name: 'PromotionalBar',
  components: { RouterLink },
  props: {
    selectedLang: { type: String, required: true },
    fixed: { type: Boolean, default: false },
    // ya no usamos dismissible para que nadie pueda cerrar la barra
    tone: { type: String, default: 'brand' } // 'brand' | 'neutral' | 'warning'
  },
  computed: {
    content () {
      const byLang = (promotionalText && promotionalText[this.selectedLang]) || promotionalText.es
      if (!byLang || (!byLang.text && !byLang.linkText)) return null
      return byLang
    },
    linkUrl () {
      if (this.content && this.content.linkUrl) return this.content.linkUrl
      return promotionalText.linkUrl || '#'
    },
    isExternal () {
      return typeof this.linkUrl === 'string' && /^(https?:)?\/\//i.test(this.linkUrl)
    },
    ariaLabel () {
      const map = { es: 'Barra promocional', en: 'Promotional bar', de: 'Werbebanner' }
      return map[this.selectedLang] || map.es
    },
    internalLabel () {
      const map = { es: 'abre dentro del sitio', en: 'opens within site', de: 'öffnet innerhalb der Website' }
      return map[this.selectedLang] || map.es
    },
    externalLabel () {
      const map = { es: 'se abre en nueva pestaña', en: 'opens in a new tab', de: 'öffnet in neuem Tab' }
      return map[this.selectedLang] || map.es
    },
    toneClass () {
      if (this.tone === 'neutral') return 'promotional-bar--neutral'
      if (this.tone === 'warning') return 'promotional-bar--warning'
      return 'promotional-bar--brand'
    }
  }
}
</script>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
  .slide-fade-enter-active,
  .slide-fade-leave-active { transition: all .22s ease; }
  .slide-fade-enter-from { opacity: 0; transform: translateY(-6px); }
  .slide-fade-leave-to   { opacity: 0; transform: translateY(-6px); }
}

.promotional-bar {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  color: #fff;
  font-family: 'JetBrainsMono-Medium', ui-monospace, SFMono-Regular, Menlo, monospace;
  overflow: hidden;
  isolation: isolate;
}
.promotional-bar--fixed {
  position: sticky;
  top: 0;
  z-index: 1030;
  box-shadow: 0 6px 16px rgba(0,0,0,.14);
}

.promotional-bar--brand {
  background: linear-gradient(135deg, #2c74b8 0%, #1b4fa0 55%, #0d3f91 100%);
}
.promotional-bar--neutral {
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
}
.promotional-bar--warning {
  background: linear-gradient(135deg, #f59e0b 0%, #b45309 100%);
}

.promo-flare {
  content: "";
  position: absolute;
  inset: -60% -30% auto -30%;
  height: 140%;
  background: radial-gradient(ellipse at center, rgba(255,255,255,.22), rgba(255,255,255,0) 60%);
  transform: rotate(-8deg);
  opacity: .45;
  pointer-events: none;
  z-index: -1;
  animation: flare 6s ease-in-out infinite alternate;
}
@keyframes flare {
  from { transform: rotate(-8deg) translateX(-4%); opacity: .35; }
  to   { transform: rotate(-4deg) translateX(2%);  opacity: .5; }
}

.promo-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: .8rem 1rem;
}
.promo-icon {
  display: grid;
  place-items: center;
  width: 28px; height: 28px;
  color: #fff;
  background: rgba(255,255,255,.18);
  border-radius: 8px;
  transform: rotate(-8deg);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.22);
}

.promo-text {
  line-height: 1.35;
  margin: 0;
  text-wrap: balance;
}

.promo-cta {
  --ring: rgba(255,255,255,.75);
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  color: #0b2a55;
  background: #fff;
  border: 0;
  padding: .45rem .85rem;
  border-radius: 999px;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 3px 10px rgba(0,0,0,.15);
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
}
.promo-cta:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,.22); }
.promo-cta:active { transform: translateY(0); box-shadow: 0 2px 8px rgba(0,0,0,.18); }
.promo-cta:focus-visible { outline: 2px solid var(--ring); outline-offset: 2px; }
.promo-cta__icon { transform: translateX(0); transition: transform .2s ease; }
.promo-cta:hover .promo-cta__icon { transform: translateX(2px); }

/* Responsive */
@media (max-width: 840px) {
  .promo-inner {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "icon text"
      ".    cta";
  }
  .promo-icon { grid-area: icon; }
  .promo-text { grid-area: text; }
  .promo-cta  { grid-area: cta; justify-self: center; width: max-content; }
}

@media (max-width: 560px) {
  .promotional-bar { padding: 10px 12px; }
  .promo-inner {
    grid-template-columns: 1fr;
    grid-template-areas:
      "text"
      "cta";
    gap: .6rem .8rem;
  }
  .promo-icon { display: none; }
  .promo-text { font-size: .92rem; }
  .promo-cta  { width: 100%; justify-content: center; padding: .5rem 1rem; }
}
</style>
